/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PropsWithChildren, useCallback, useState } from 'react'
import { selectFile } from '../../../../client/js/lib/download'
import DeleteModal from '../../../../client/js/components/modals/DeleteModal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormattedDate, FormattedList, FormattedMessage, FormattedTime, useIntl } from 'react-intl'
import FormattedFileSize from '../../../../client/js/components/FormattedFileSize'
import { Button, Group, Input, InputWrapper, TextInputProps } from '@mantine/core'
import LabelledValue from '../LabelledValue'

export type FileInputProps = PropsWithChildren & Omit<TextInputProps, 'accept'> & {
  accept?: string[],
  contentLength?: number,
  error?: string,
  lastModified?: string | number | Date,
  maxSize?: number,
  onChange (file?: File): void,
  onDelete (): Promise<unknown>,
  onDownload (): Promise<unknown>,
  onUpload (file: File): Promise<File>,
}

function FileInput (props: FileInputProps) {
  const {
    accept,
    children,
    contentLength,
    description,
    disabled,
    error,
    label,
    lastModified,
    maxSize,
    name,
    onDelete,
    onDownload,
    onChange,
    onUpload,
    required,
    value
  } = props

  const intl = useIntl()

  const downloader = useQuery({
    enabled: false,
    queryKey: ['downloadFile', name, value],
    queryFn: onDownload
  })

  const deleter = useMutation({
    mutationKey: ['deleteFile'],
    mutationFn: onDelete,
    onSuccess: () => onChange(null)
  })

  const uploader = useMutation({
    mutationKey: ['uploadFile'],
    mutationFn: onUpload,
    onSuccess: (file: File) => onChange(file)
  })
  const { mutate: upload } = uploader

  const [isDelete, setIsDelete] = useState<boolean>(false)

  const handleUpload = useCallback(() => {
    selectFile(upload, { accept: accept?.join(',') })
  }, [accept, upload])

  const errorMessage = error ||
    deleter.error?.message ||
    uploader.error?.message ||
    downloader.error?.message

  return (
    <InputWrapper
      error={errorMessage}
      description={description}
      label={label}
      required={required}
    >
      <Group gap="xs" fz="xs" c="dimmed">
        {lastModified != null || contentLength != null
          ? <>
            {lastModified != null
              ? <span>
                <FormattedMessage
                  id="uploaded_at"
                  defaultMessage="Transféré le {date} à {time}"
                  values={{
                    date: <FormattedDate value={lastModified} />,
                    time: <FormattedTime value={lastModified} />
                  }}
                />
              </span>
              : null}

            {contentLength != null
              ? <FormattedFileSize value={contentLength} />
              : null}
          </>
          : <>
            {maxSize > 0
              ? <LabelledValue
                label={intl.formatMessage({
                  id: 'max_size',
                  defaultMessage: 'Taille maximale'
                })}
                value={<FormattedFileSize value={maxSize} />}
              />
              : null}

            {accept != null && accept.length > 0
              ? <LabelledValue
                label={intl.formatMessage({
                  id: 'accepted_formats',
                  defaultMessage: 'Formats acceptés'
                })}
                value={<FormattedList value={accept} />}
              />
              : null}
          </>}
      </Group>

      <Group gap="xs" align="end" wrap="nowrap">
        <Input
          disabled
          value={value != null ? value : ''}
          style={{ flexGrow: 1 }}
        />

        {children}

        {value != null
          ? <>
            <Button
              disabled={disabled || downloader.isFetching}
              loading={downloader.isFetching}
              onClick={() => {
                downloader.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faDownload}
              />
            </Button>
            <Button
              disabled={disabled || deleter.isPending}
              loading={deleter.isPending}
              onClick={() => {
                setIsDelete(true)
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          </>
          : <Button
            disabled={disabled || uploader.isPending}
            loading={uploader.isPending}
            onClick={handleUpload}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faUpload}
            />
          </Button>
        }
      </Group>

      <DeleteModal
        opened={isDelete}
        label={String(value)}
        onConfirm={() => deleter.mutateAsync()}
        onClose={() => {
          setIsDelete(false)
        }}
      />
    </InputWrapper>
  )
}

export default FileInput
